// 2) Third-party library imports
import {
    all,
    call,
    delay,
    fork,
    put,
    select,
    takeLatest
} from 'redux-saga/effects';
import {
    get,
} from 'lodash-es';

// 3a) Constants
import {
    maxAPIRefetchCount,
    refetchAPIDelay
} from '../../config/config';
import { 
    CHANGE_PAGE 
} from 'src/config/tableAndPageConstants';

// 3d) Utility functions
import {
    checkShouldRequestRefetch,
    getRegionallyApiResponse,
    getRegionallyGraphqlResponse,
    getRegionConfigFromList,
    getSortFieldsWithCustomFields,
    removeAppliedFiltersForApiRequest
} from '../../utils/commonFunctions';
import { DynamicObject } from '../../utils/commonInterfaces';

// 3e) Redux actions
import { deallocateRequestAction, getChangeDataErrorAction, getChangeDataRequestAction, getChangeDataSuccessAction, getChangesErrorAction, getChangesRequestAction, getChangesSuccessAction, setSelectedChangeIdRequestAction, setSelectedChangeIdSuccessAction } from './changeSlice';
import { getOrganisationActionUrl } from '../organisations/organisationSlice';
import { ApplicationState } from '../storeTypes';
import client, { post } from 'src/api';

// 3f) Project-specific modules - components / types 
import { _queries as queries } from '../../graphql/queries.graphql';

import { getRegionKeyConfig } from '../auth/authSlice';

let refetchCount = 0;
/**
 * Function called when connecting deallocate change line to the API.
 * @param param0
 */
function* handleDeallocateRequest({ payload: sagaPayload }: any) {
    const { payload, callback } = sagaPayload;
    let refetchCount = 0;

    try {
        const apiUrl: string | undefined = yield select(
            getOrganisationActionUrl
        );

        const res: DynamicObject = yield call(
            getRegionallyApiResponse,
            apiUrl,
            '/change/reverse',
            'POST',
            payload
        );

        if (callback) {
            const response = {
                ...res,
                IsSuccess: true,
            };

            callback(response);
        }
    }
    catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        }
        else {
            console.error('An unknown error occured.');
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;

            yield delay(refetchAPIDelay);
            yield put(deallocateRequestAction(payload, callback));
        } else {
            if (callback) {
                let returnData: any;

                if (err instanceof Error && 'response' in err && (err as any).response.body) {
                    returnData = JSON.parse((err as any).response.body);
                } else if (err instanceof Error) {
                    returnData = { Messages: [err.message] };
                } else {
                    returnData = { Messages: ['An unknown error occurred.'] };
                }

                returnData.IsSuccess = false;
                callback(returnData);
                //const returnData = get(err.response, 'data')
                //    ? err.response.data
                //    : { Messages: [err.message] };
                //returnData.IsSuccess = false;
                //callback(returnData);
            }
        }
    }
}

export const getSelectedChangeId = (state: ApplicationState) =>
    state.changes.activeData.selectedId;

export const getChangeData = (state: ApplicationState) =>
    state.changes.activeData;

/**
 * Function that sets the selected change id in the redux state for reference.
 * @param param0
 */
function* handleSetSelectedChangeIdRequest({ payload }: any) {
    const { changeId, callback } = payload;
    yield put(setSelectedChangeIdSuccessAction(changeId));
    callback();
}

function* handleGetChangesRequest({ payload }: any) {
    const errorMessage = 'Error fetching change list. Please try again later.';
    try {
        const {
            filters,
            pageSize,
            currentPage,
        } = payload;

        const cleanFilters = removeAppliedFiltersForApiRequest(
            filters,
            true,
            'changelines',
            true,
        );

        const res: DynamicObject = yield call(
            [client, client.graphql],
            {
                query: queries.GET_CHANGE_LINES_FOR_COMPANY,
                variables: {
                    ...cleanFilters,
                    PageSize: pageSize,
                    Skip: currentPage * CHANGE_PAGE.pageSize,
                    FetchResource: true
                }
            }
        );

        const { ChangeLines } = get(res.data, 'GetChangeLinesForCompany');
        if (ChangeLines) {
            const responsePayload = {
                data: ChangeLines,
                pageData: {
                    pageSize: pageSize,
                    currentPage: currentPage,
                    hasNextPage:
                        !(ChangeLines.length < pageSize) &&
                        !(pageSize < CHANGE_PAGE.pageSize),
                },
            };

            refetchCount = 0;
            yield put(getChangesSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        if (
            refetchCount <= maxAPIRefetchCount &&
            checkShouldRequestRefetch(err)
        ) {
            refetchCount++;
            yield delay(refetchAPIDelay);
            yield put(getChangesRequestAction({ ...payload, refetchCount }));
        } else {
            yield put(getChangesErrorAction([errorMessage]));
        }
    }
}

function* handleGetChangeDataRequest({ changeId }: any) {
    const errorMessage = `Error fetching credit changes list. Please try again later.`;
    try {
        const changeData: DynamicObject = yield select(
            (state: ApplicationState) => state.changes.data
        );

        const selectedChangeId: DynamicObject = yield select(
            (state: ApplicationState) => state.changes.activeData.selectedId
        );

        const getSelectedChangeData = changeData.filter(
            (item: any) => item.Id === selectedChangeId
        );

        if (getSelectedChangeData) {
            const responsePayload = {
                record: getSelectedChangeData
            };

            yield put(getChangeDataSuccessAction(responsePayload));
        }
    } catch (err) {
        if (err instanceof Error) {
            console.log('Error', err);
        } else {
            console.error('An unknown error occured.', err);
        }

        yield put(getChangeDataErrorAction([errorMessage]));
    }
}


function* watchDeallocateCreditRequest() {
    yield takeLatest(
        deallocateRequestAction.type,
        handleDeallocateRequest
    );
}

function* watchSetSelectedChangeIdRequest() {
    yield takeLatest(
        setSelectedChangeIdRequestAction.type,
        handleSetSelectedChangeIdRequest
    );
}

function* watchGetChangeDataRequest() {
    yield takeLatest(
        getChangeDataRequestAction.type,
        handleGetChangeDataRequest
    );
}

function* watchGetChangesRequest() {
    yield takeLatest(
        getChangesRequestAction.type,
        handleGetChangesRequest
    );
}

// We can also use `fork()` here to split our saga into multiple watchers.
function* changesSaga() {
    yield all([
        fork(watchDeallocateCreditRequest),
        fork(watchGetChangesRequest),
        fork(watchSetSelectedChangeIdRequest),
        fork(watchGetChangeDataRequest),
    ]);
}

export default changesSaga;
